import React, { useEffect, useMemo, useRef, useImperativeHandle, forwardRef } from "react";
import { DataSet } from "vis-data";
import { Timeline, TimelineOptions, DataGroup, DataItem } from "vis-timeline/standalone";
import "vis-timeline/styles/vis-timeline-graph2d.min.css";
import "./styles/gantt.vis.css";
import { MapObject } from "~/features/map-view/ui/map-view";
import { ConnectProto } from "@racoon/api";
import { IcAi } from "@racoon/icons";
import { renderToString } from "react-dom/server";
import { frameBounds, type ShiftSpan } from "./shift-span";
import { escapeHtml } from "~/shared/lib/escape-html";
import { ROUTE_STATUS_TONE, STATUS_TONE_HEX, getEnumName } from "@racoon/lib";

export { escapeHtml };

export type Vehicle = {
  id: string;
  label: string;
  visible_to_ai?: boolean;
  driver_on_shift_id?: number | string;
  /** The truck a row's label opens, when the row isn't a truck itself. */
  truckId?: string;
};

export type GanttSection = { id: string; label: string; badge: string; rowIds: string[] };

export type RouteItem = {
  id: string;
  /** Set when the bar's id isn't the route's own: a route drawn on several rows. */
  routeId?: string;
  vehicleId: string;
  title: string;
  location?: string;
  start: string | Date;
  end: string | Date;
  status?: string | ConnectProto.RouteStatus;
  color?: string;
  segmentLabel?: string;
  countBadge?: string;
  finishedJobs?: number;
  /** All of the route's jobs, any status; a row's label sums them. Unset: not counted. */
  jobCount?: number;
  routeCategory?: ConnectProto.RouteCategory;
  ownerOrganizationId?: string;
  /** Id of the DriverOnShift this route belongs to — used to group/highlight on hover. */
  driverOnShiftId?: string | number;
  /** Translated shift status shown (colored) in the hover frame header, e.g. "Смена активна". */
  shiftStatusLabel?: string;
  /** Tailwind text-color class for the shift status text in the header. */
  shiftStatusClass?: string;
  /** Who or what the shift is (driver, or truck in the driver view), white in the hover header. */
  shiftCaption?: string;
  /** The shift's own span for the hover frame. */
  shiftSpan?: ShiftSpan;
};

type Props = {
  vehicles: Vehicle[];
  items: RouteItem[];
  sections?: GanttSection[];
  labelWidth?: number;
  onItemDateChange?: (payload: { id: string; start: string; end: string }) => void;
  onItemMoveToGroup?: (payload: { id: string; vehicleId: string }) => void;
  onItemClick?: (payload: { type: MapObject["type"]; id: string }) => void;
  onToggleAiVisibility?: (driverOnShiftId: number) => void;
  initialStart?: Date;
  initialEnd?: Date;
  selectedItemId?: number | string | null;
  isAiEnabled?: boolean;
  onInitialDrawComplete?: () => void;
  /** Show the day-level (major) axis labels — useful when the timeline spans several days. */
  showMajorLabels?: boolean;
  /** Translated word for "shift", shown in the hover header when no status label is available. */
  shiftWord?: string;
  /** Translated tooltip for the AI toggle when the shift is currently visible to AI. */
  aiHideTitle?: string;
  /** Translated tooltip for the AI toggle when the shift is currently hidden from AI. */
  aiShowTitle?: string;
};

// A missing or unknown status draws as a planned route, as it always has.
const routeHex = (status: RouteItem["status"]) => {
  const name =
    typeof status === "number" ? getEnumName(ConnectProto.RouteStatusSchema, status) : status;
  return STATUS_TONE_HEX[ROUTE_STATUS_TONE[name ?? ""] ?? "assigned"];
};

// The count chip is white, so on light bars it takes the ink colour and elsewhere the bar's fill.
const countColor = (status: RouteItem["status"]) => {
  const hex = routeHex(status);
  return hex.text === "#FFFFFF" ? hex.fill : hex.text;
};

// A truck's bars overlap (a day-long planned route spans its trips), and without an explicit
// order whichever bar vis inserted last wins. Shorter on top; all below the shift frame's 20.
const Z_SELECTED = 18;
const barZ = (start: string | Date, end: string | Date) => {
  const minutes = Math.max(1, (new Date(end).getTime() - new Date(start).getTime()) / 60_000);
  return Math.min(17, Math.max(2, 17 - Math.round((15 * Math.log(minutes)) / Math.log(24 * 60))));
};

// A shift can sit on several rows (a driver in two teams); the hover frame takes the hovered one.
const rowClass = (rowId: string) => `row-${rowId.replace(/[^\w-]/g, "_")}`;

function snapTo5Min(date: Date) {
  const ms5 = 5 * 60 * 1000;
  return new Date(Math.round(date.getTime() / ms5) * ms5);
}

export type GanttScheduleRef = {
  fit: () => void;
  now: () => void;
  zoomIn?: () => void;
  zoomOut?: () => void;
  setWindow: (start: Date, end: Date) => void;
};

export const GanttSchedule = forwardRef<GanttScheduleRef, Props>(function GanttSchedule(
  {
    vehicles,
    items,
    sections,
    labelWidth = 112,
    onItemDateChange,
    onItemMoveToGroup,
    onItemClick,
    onToggleAiVisibility,
    initialStart,
    initialEnd,
    selectedItemId,
    isAiEnabled = false,
    onInitialDrawComplete,
    showMajorLabels = false,
    shiftWord,
    aiHideTitle,
    aiShowTitle,
  },
  ref,
) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const timelineRef = useRef<Timeline | null>(null);

  const onToggleAiVisibilityRef = useRef(onToggleAiVisibility);

  const groupsDs = useMemo(() => {
    // 1. Calculate route count for each vehicle based on items list
    const routeCounts = new Map<string, number>();
    const jobCounts = new Map<string, number>();
    items.forEach((item) => {
      const vId = String(item.vehicleId);
      routeCounts.set(vId, (routeCounts.get(vId) ?? 0) + 1);
      if (item.jobCount !== undefined)
        jobCounts.set(vId, (jobCounts.get(vId) ?? 0) + item.jobCount);
    });

    // 2. Sort vehicles: by route count descending, then alphabetically by label
    const sortedVehicles = [...vehicles].sort((a, b) => {
      const countA = routeCounts.get(String(a.id)) ?? 0;
      const countB = routeCounts.get(String(b.id)) ?? 0;

      if (countB !== countA) {
        return countB - countA;
      }
      return a.label.localeCompare(b.label);
    });

    // Plain rows rather than vis nested groups, which can't be kept from collapsing on click.
    const order = new Map<string, number>();
    const rank = new Map(sortedVehicles.map((v, index) => [v.id, index]));
    for (const section of sections ?? []) {
      order.set(section.id, order.size);
      const rows = [...section.rowIds].sort((a, b) => (rank.get(a) ?? 0) - (rank.get(b) ?? 0));
      rows.forEach((id) => order.set(id, order.size));
    }
    sortedVehicles.forEach((v) => order.has(v.id) || order.set(v.id, order.size));

    const headings = (sections ?? []).map((section) => ({
      id: section.id,
      order: order.get(section.id)!,
      className: "gantt-section",
      content: `<div class="gantt-section__label"><span>${escapeHtml(section.label)}</span><span class="gantt-section__badge">${escapeHtml(section.badge)}</span></div>`,
    }));

    return new DataSet<DataGroup & { order: number }>([
      ...headings,
      ...sortedVehicles.map((v) => {
        const isAiVisible = v.visible_to_ai;
        const iconBgColor = isAiVisible ? "#9166FF" : "transparent";
        const iconColorClass = isAiVisible ? "text-white" : "text-icon-secondary";

        // HTML for AI button (rendered only if isAiEnabled)
        const aiButtonHtml = isAiEnabled
          ? `
                                <div 
                                class="ai-toggle-btn w-[32px] h-full border-r border-white flex items-center justify-center cursor-pointer hover:bg-white/10 transition-colors"
                                style="background-color: ${iconBgColor}"
                                data-dos-id="${v.driver_on_shift_id ?? ""}"
                                title="${escapeHtml(isAiVisible ? aiHideTitle : aiShowTitle)}"
                                >
                                ${renderToString(<IcAi width={24} height={24} className={iconColorClass} />)}
                                </div>
                            `
          : "";
        const containerPaddingClass = isAiEnabled ? "pr-3" : "px-3";

        return {
          id: v.id,
          order: order.get(v.id)!,
          content: `
                  <div class="text-text-heading rounded-l-[8px] h-[32px] flex items-center text-[14px] gap-2 py-0 ${containerPaddingClass} bg-border-primary overflow-hidden" style="width:${labelWidth}px" title="${escapeHtml(v.label)}">
                    ${aiButtonHtml}
                    <span class="truncate flex-1">${escapeHtml(v.label)}</span>
                    ${jobCounts.has(v.id) ? `<span class="gantt-row__badge">${jobCounts.get(v.id)}</span>` : ""}
                  </div>
                `,
        };
      }),
    ]);
  }, [vehicles, items, sections, labelWidth, isAiEnabled, aiHideTitle, aiShowTitle]);

  const itemsDs = useMemo(() => {
    return new DataSet<DataItem & { vehicleId: string; dosId?: string; routeId: string }>(
      items.map((r) => {
        const routeId = r.routeId ?? r.id;
        const isSelected = routeId === String(selectedItemId);
        const dosId = r.driverOnShiftId !== undefined ? String(r.driverOnShiftId) : undefined;
        return {
          id: r.id,
          group: r.vehicleId,
          start: r.start,
          end: r.end,
          type: "range",
          xss: { disabled: true },
          dosId,
          routeId,

          className: `${r.id.startsWith("gap-") ? "gap-item" : ""} ${isSelected ? "selected-item" : ""} ${dosId ? `dos-${dosId}` : ""} ${rowClass(r.vehicleId)}`,
          content: `
          
              <span >${escapeHtml(r.title)}</span>
                 <h5>
     ${r.segmentLabel ? `<span>${escapeHtml(r.segmentLabel)}</span>` : ""}
                  ${r.location ? `<span style="opacity:.8">| ${escapeHtml(r.location)}</span>` : ""}
                  ${r.countBadge ? `<p style="color:${countColor(r.status)}">${escapeHtml(r.countBadge)}</p>` : ""}

                 </h5>
          `,
          vehicleId: r.vehicleId,
          style: `
            height:32px;
            display:inline-flex;
            justify-content: space-between;
            align-items:center;
            gap:6px;
            padding:0;
            border-radius:8px;
            font-size:12px;
            font-weight:500;
            color:${routeHex(r.status).text};
            background-color:${routeHex(r.status).fill}!important;
            margin-right:16px;
            cursor:pointer;
            z-index:${isSelected ? Z_SELECTED : barZ(r.start, r.end)};
            ${isSelected ? "box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.5)!important;" : ""}
          `,
        };
      }),
    );
  }, [items, selectedItemId]);

  const onItemMoveToGroupRef = useRef(onItemMoveToGroup);
  const onItemDateChangeRef = useRef(onItemDateChange);
  const onItemClickRef = useRef(onItemClick);
  const vehiclesRef = useRef(vehicles);
  const itemsDsRef = useRef(itemsDs);
  const itemsRef = useRef(items);
  const shiftWordRef = useRef(shiftWord);
  const onInitialDrawCompleteRef = useRef(onInitialDrawComplete);

  useEffect(() => {
    onItemMoveToGroupRef.current = onItemMoveToGroup;
    onItemDateChangeRef.current = onItemDateChange;
    onItemClickRef.current = onItemClick;
    onToggleAiVisibilityRef.current = onToggleAiVisibility;
    vehiclesRef.current = vehicles;
    itemsDsRef.current = itemsDs;
    itemsRef.current = items;
    shiftWordRef.current = shiftWord;
    onInitialDrawCompleteRef.current = onInitialDrawComplete;
  });

  useEffect(() => {
    if (!containerRef.current) return;

    const now = new Date();
    const options: TimelineOptions = {
      locale: "ru",
      stack: false,
      multiselect: false,
      horizontalScroll: true,
      zoomKey: "ctrlKey",
      zoomable: false,
      groupOrder: "order",
      showMajorLabels,
      showTooltips: true,
      stackSubgroups: false,

      zoomMin: 1000 * 60 * 30,
      zoomMax: 1000 * 60 * 60 * 24 * 14,
      editable: {
        add: false,
        remove: false,
        updateTime: false,
        updateGroup: false,
      },
      xss: { disabled: true },

      margin: { item: 12, axis: 6 },
      orientation: "top",
      groupHeightMode: "fitItems" as any,
      groupEditable: false,
      snap: snapTo5Min,
      showCurrentTime: true,
      timeAxis: { scale: "hour", step: 1 },
      start: initialStart ?? new Date(now.getFullYear(), now.getMonth(), now.getDate(), 7, 0),
      end: initialEnd ?? new Date(now.getFullYear(), now.getMonth(), now.getDate(), 18, 30),
      tooltip: { followMouse: true, overflowMethod: "cap" },
      groupTemplate: (group) => {
        const el = document.createElement("div");
        el.innerHTML = String(group?.content ?? "");
        return el;
      },
      onInitialDrawComplete: () => {
        if (onInitialDrawCompleteRef.current) {
          onInitialDrawCompleteRef.current();
        } else {
          timelineRef.current?.moveTo(new Date(), {
            animation: { duration: 300, easingFunction: "easeInOutQuad" },
          });
        }
      },
    };
    const timeline = new Timeline(containerRef.current, itemsDs, groupsDs, options);
    timelineRef.current = timeline;

    (timeline as any).on("moving", (item: any, callback: any) => {
      if (item && callback) {
        item.start = snapTo5Min(new Date(item.start));
        item.end = snapTo5Min(new Date(item.end));
        callback(item);
      }
    });

    timeline.on("moved", (item: any) => {
      const newGroup = String(item.group);
      if (newGroup !== item.vehicleId) {
        item.vehicleId = newGroup;
        onItemMoveToGroupRef.current?.({
          id: String(item.id),
          vehicleId: newGroup,
        });
      }
      onItemDateChangeRef.current?.({
        id: String(item.id),
        start: new Date(item.start).toISOString(),
        end: new Date(item.end).toISOString(),
      });
    });

    timeline.on("click", (properties: any) => {
      const target = properties.event.target as HTMLElement;
      const toggleBtn = target.closest(".ai-toggle-btn");

      if (toggleBtn) {
        const dosId = toggleBtn.getAttribute("data-dos-id");
        if (dosId && onToggleAiVisibilityRef.current) {
          properties.event.stopPropagation();
          onToggleAiVisibilityRef.current(Number(dosId));
          return;
        }
      }

      if (properties.item) {
        const item = itemsDsRef.current.get(properties.item);

        if (item && typeof item === "object" && "routeId" in item) {
          onItemClickRef.current?.({ id: String(item.routeId), type: "route" });
        }
        return;
      }

      if (properties.group && properties.what === "group-label") {
        const vehicle = vehiclesRef.current.find((v) => v.id === properties.group);
        if (vehicle) {
          onItemClickRef.current?.({ type: "truck", id: vehicle.truckId ?? vehicle.id });
        }
      }
    });

    // Hover mode: draw a frame over one driver-on-shift's span (the shift
    // container) plus a header badge. The route bars themselves are left untouched.
    // The frame lives inside a clip wrapper pinned to the center panel and its
    // horizontal bounds are clamped to that panel, so a shift longer than the
    // visible window still shows a frame (and its badge) instead of being cut off.
    const container = containerRef.current!;
    container.style.position = "relative";
    const overlay = document.createElement("div");
    overlay.className = "dos-frame-clip";
    overlay.style.display = "none";
    overlay.innerHTML = `<div class="dos-frame"><span class="dos-frame__badge"></span></div>`;
    container.appendChild(overlay);
    const frameEl = overlay.querySelector(".dos-frame") as HTMLElement;
    const badgeEl = overlay.querySelector(".dos-frame__badge") as HTMLElement;

    // Currently framed shift; used to skip re-layout while hovering within one shift.
    let activeDosId: string | null = null;
    let activeRow = "";

    const clearDosHighlight = () => {
      activeDosId = null;
      overlay.style.display = "none";
    };

    const PAD_X = 6;
    const PAD_Y = 3;
    // Below this the visible slice of the frame is too thin to read as a container.
    const MIN_VISIBLE_W = 24;

    /**
     * (Re)position the frame for `dosId` against the current window and scroll
     * position. Horizontal bounds come from the shift's time span (not from the
     * rendered bars, which vis drops once they leave the window) and are clamped
     * to the center panel, so long shifts stay visible and keep their badge.
     * Returns false when the shift has no visible slice left.
     */
    const layoutDosFrame = (dosId: string, row: string): boolean => {
      const shiftItems = itemsRef.current.filter(
        (it) => String(it.driverOnShiftId) === dosId && it.vehicleId === row,
      );
      if (shiftItems.length === 0) return false;
      const anyEl = container.querySelector<HTMLElement>(
        `.vis-item.dos-${CSS.escape(dosId)}.${rowClass(row)}`,
      );
      // Vertical bounds take the whole row (group) so the frame reads as the shift
      // container, not a single route.
      const rowEl = anyEl?.closest<HTMLElement>(".vis-group") ?? anyEl;
      if (!rowEl) return false;

      const contRect = container.getBoundingClientRect();
      const centerRect =
        container.querySelector<HTMLElement>(".vis-panel.vis-center")?.getBoundingClientRect() ??
        contRect;
      const win = timeline.getWindow();
      const winStart = new Date(win.start).getTime();
      const span = new Date(win.end).getTime() - winStart;
      if (!(span > 0) || centerRect.width <= 0) return false;

      const routes = { start: Infinity, end: -Infinity };
      shiftItems.forEach((it) => {
        routes.start = Math.min(routes.start, new Date(it.start).getTime());
        routes.end = Math.max(routes.end, new Date(it.end).getTime());
      });
      const [minT, maxT] = frameBounds(shiftItems[0].shiftSpan, routes, Date.now());
      const toX = (t: number) => ((t - winStart) / span) * centerRect.width;
      const rawL = toX(minT) - PAD_X;
      const rawR = toX(maxT) + PAD_X;
      const visL = Math.max(0, rawL);
      const visR = Math.min(centerRect.width, rawR);
      if (visR - visL < MIN_VISIBLE_W) return false;

      // Clip wrapper is pinned to the center panel so nothing spills over the left
      // labels column.
      overlay.style.left = `${centerRect.left - contRect.left}px`;
      overlay.style.top = "0px";
      overlay.style.width = `${centerRect.width}px`;
      // Full content height (not just the viewport) so rows scrolled below the fold
      // aren't clipped by the wrapper's overflow.
      overlay.style.height = `${Math.max(contRect.height, container.scrollHeight)}px`;

      const rowRect = rowEl.getBoundingClientRect();
      frameEl.style.left = `${visL}px`;
      frameEl.style.top = `${rowRect.top - contRect.top + PAD_Y}px`;
      frameEl.style.width = `${visR - visL}px`;
      frameEl.style.height = `${rowRect.height - PAD_Y * 2}px`;
      // Flatten the edge on whichever side continues past the viewport.
      frameEl.classList.toggle("dos-frame--cut-left", rawL < visL - 0.5);
      frameEl.classList.toggle("dos-frame--cut-right", rawR > visR + 0.5);

      // Keep the badge inside the panel — it sits above the frame's left edge, which
      // for a long shift can be far off-screen.
      const badgeW = badgeEl.offsetWidth;
      const badgeL = Math.max(0, Math.min(8, centerRect.width - visL - badgeW));
      badgeEl.style.left = `${badgeL}px`;
      return true;
    };

    const highlightDos = (dosId: string, row: string) => {
      const shiftItems = itemsRef.current.filter(
        (it) => String(it.driverOnShiftId) === dosId && it.vehicleId === row,
      );
      if (shiftItems.length === 0) {
        clearDosHighlight();
        return;
      }
      // Header: colored shift status + white driver name. All routes of a shift
      // carry the same shift data, so the first item is representative. Set before
      // layout so the badge can be measured for clamping.
      const rep = shiftItems[0];
      const statusLabel = rep.shiftStatusLabel;
      const statusClass = rep.shiftStatusClass ?? "";
      const driverName = rep.shiftCaption ?? "";
      const head = statusLabel
        ? `<span class="${statusClass}">${escapeHtml(statusLabel)}</span>  <span>S${escapeHtml(dosId)}</span>`
        : `<span>${escapeHtml(shiftWordRef.current)} S${escapeHtml(dosId)}</span>`;
      badgeEl.innerHTML = driverName ? `${head}  <span>${escapeHtml(driverName)}</span>` : head;
      overlay.style.display = "block";
      if (!layoutDosFrame(dosId, row)) {
        clearDosHighlight();
        return;
      }
      activeDosId = dosId;
      activeRow = row;
    };

    // Keep the frame glued to the shift while the user pans/zooms or scrolls rows.
    // Pan/zoom/scroll fire far faster than paint, and a relayout costs DOM queries
    // plus getBoundingClientRect, so collapse bursts into one pass per frame —
    // coalescing rather than time-throttling, so the final position is never dropped.
    let relayoutRaf = 0;
    const relayoutActive = () => {
      if (!activeDosId || relayoutRaf) return;
      relayoutRaf = requestAnimationFrame(() => {
        relayoutRaf = 0;
        if (!activeDosId) return;
        overlay.style.display = layoutDosFrame(activeDosId, activeRow) ? "block" : "none";
      });
    };

    timeline.on("itemover", (props: any) => {
      const item = props?.item != null ? itemsDsRef.current.get(props.item) : null;
      const { dosId, vehicleId } = (item ?? {}) as { dosId?: string; vehicleId?: string };
      if (!dosId || !vehicleId) {
        clearDosHighlight();
        return;
      }
      if (String(dosId) === activeDosId && vehicleId === activeRow) {
        relayoutActive();
        return;
      }
      highlightDos(String(dosId), vehicleId);
    });
    timeline.on("itemout", () => clearDosHighlight());
    timeline.on("rangechange", relayoutActive);
    timeline.on("rangechanged", relayoutActive);

    const scrollHost = container.parentElement;
    scrollHost?.addEventListener("scroll", relayoutActive, { passive: true });
    const resizeObserver =
      typeof ResizeObserver !== "undefined" ? new ResizeObserver(relayoutActive) : null;
    resizeObserver?.observe(container);

    const tick = setInterval(() => timeline.setCurrentTime(new Date()), 60_000);

    return () => {
      clearInterval(tick);
      if (relayoutRaf) cancelAnimationFrame(relayoutRaf);
      scrollHost?.removeEventListener("scroll", relayoutActive);
      resizeObserver?.disconnect();
      overlay.remove();
      timeline.destroy();
      timelineRef.current = null;
    };
  }, []);

  useEffect(() => {
    if (!timelineRef.current) return;
    timelineRef.current.setGroups(groupsDs);
    timelineRef.current.setItems(itemsDs);
  }, [groupsDs, itemsDs]);

  // Keep options that can change after mount in sync (timeline is created once).
  useEffect(() => {
    timelineRef.current?.setOptions({ showMajorLabels });
  }, [showMajorLabels]);

  useImperativeHandle(ref, () => ({
    fit() {
      timelineRef.current?.fit();
    },
    now() {
      const n = new Date();
      timelineRef.current?.moveTo(n, { animation: { duration: 300 } as any });
    },

    setWindow(start: Date, end: Date) {
      timelineRef.current?.setWindow(start, end, { animation: true });
    },
  }));

  return (
    <div className="w-full h-full overflow-y-auto">
      <div ref={containerRef} className="h-full" />
    </div>
  );
});
